import { Component, Host, Prop, h } from '@stencil/core';
import { formatSmartDate } from '@utils/dateUtils';
import { getBackgroundStyle } from '@utils/imageUtils';
import { getGameAbbr } from '@utils/gameUtils';
import { SportslinePickProps } from 'src/types';
import { formatOdds } from '@utils/bettingUtils';
import { toHexColor } from '@utils/colorUtils';
import { getTeamName } from '@utils/expertPickUtils';

@Component({
  tag: 'sportsline-free-expert-pick-selection-header',
  styleUrl: 'sportsline-free-expert-pick-selection-header.css',
  shadow: true,
})
export class SportslinePickCardHeader {
  /**
   * The pick details to be displayed in the card.
   */
  @Prop() pick: Pick<SportslinePickProps, 'gameInfo' | 'selection' | 'isBettable'>;

  private get getSelectionImage() {
    const selection = this.pick.selection;
    if ('player' in selection && Boolean(selection.player)) {
      const playerTeam = [this.pick.gameInfo.awayTeam, this.pick.gameInfo.homeTeam].find(team => team.id === selection.player.teamId);
      const playerId = this.pick.isBettable ? '-1' : selection.player.id;
      return (
        <sportsline-player-avatar
          style={{
            '--sportsline-player-avatar-background-color': toHexColor({ color: playerTeam?.colorSecondaryHex }),
          }}
          playerId={playerId}
          size="large"
          alt={selection.player.name}
        />
      );
    }
    if ('team' in selection && Boolean(selection.team)) {
      return <sportsline-team-logo teamId={selection.team.id} alt={getTeamName(selection.team)} size="large" />;
    }
    if ('overUnder' in selection && Boolean(selection.overUnder)) {
      return <sportsline-over-under-image size="large" selection={selection.overUnder} />;
    }
  }

  private get getTeams() {
    const { selection, gameInfo } = this.pick;

    // get the team based on the selection
    const teams = [gameInfo.homeTeam, gameInfo.awayTeam];
    const team = teams.find(t => ('team' in selection && t.id === selection.team.id) || ('player' in selection && t.id === selection.player.teamId));
    // if the selection is a total or if there is no team, return both teams
    const isTotal = selection.pickType === 'gameProp' && Boolean(selection.overUnder);
    // if the selection is a total or if there is no team selected, return both teams
    return isTotal || !Boolean(team) ? teams : [team];
  }

  private get getFormattedDate() {
    return formatSmartDate(new Date(this.pick.gameInfo.scheduledTime));
  }

  private get getBackground() {
    return getBackgroundStyle(this.getTeams.map(team => team.colorHexDex));
  }

  render() {
    if (!Boolean(this.pick?.selection) || !Boolean(this.pick.gameInfo)) return null;

    return (
      <Host>
        <div class="sportsline-free-expert-pick-selection-header" style={{ '--teams-background': this.getBackground }}>
          <div class="sportsline-free-expert-pick-selection-header--team-logos">
            {this.getTeams.map((team, index) => (
              <sportsline-team-logo key={index} teamId={team.id} alt={getTeamName(team)} />
            ))}
          </div>

          <div class="sportsline-free-expert-pick-selection-header--container">
            {this.getSelectionImage}
            <div class="sportsline-free-expert-pick-selection-header--details">
              <span class="sportsline-free-expert-pick-selection-header--details-label">
                {this.pick.selection.label}
                <span class="sportsline-free-expert-pick-selection-header--details-odds">{formatOdds(this.pick.selection.odds)}</span>
              </span>
              {this.pick.selection.pickType === 'playerProps' && (
                <span class="sportsline-free-expert-pick-selection-header--details-sub-label">{this.pick.selection.player.name}</span>
              )}
              <span class="sportsline-free-expert-pick-selection-header--details-sub-label">{this.pick.selection.subLabel}</span>
              <span class="sportsline-free-expert-pick-selection-header--details-game-info">
                {getGameAbbr(this.pick.gameInfo)} • {this.getFormattedDate}
              </span>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
